import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@nfp-tracker/components";
import { SUPPORT_MAILTO_URL } from "@nfp-tracker/utils";
import { CheckCircle2, Gift } from "lucide-react";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  UseWhatsNewResult,
  WhatsNewItemView,
} from "../../hooks/useWhatsNew";
import { trackEvent } from "../../integrations/analytics/client";
import { NewBadge } from "../NewBadge";
import { SupportChatContext } from "../SupportChatContext";
import { ChecklistRow } from "./ChecklistRow";

type ReleaseReviewStepProps = {
  whatsNew: UseWhatsNewResult;
};

/**
 * Permanent Getting Started step "Review the latest release". It always opens the current
 * WHATS_NEW_RELEASE (constants/whatsNew.ts): feature cards with a CTA and "Mark as done", then
 * plain-text release notes.
 *
 * Completion rule: the step is done once every required card is done (its CTA, visiting the
 * feature, or the card's "Mark as done"), or via the step's own "Mark as done", which marks every
 * card done. `optional` cards (the instructor ones) can be completed but don't gate the step.
 * Opening the dialog doesn't complete it, so the step stays open to revisit until the user has
 * worked through the cards. Done cards stay listed, muted.
 */
export const ReleaseReviewStep = ({ whatsNew }: ReleaseReviewStepProps) => {
  const navigate = useNavigate();
  // Read the context directly (not useSupportChatPanel) so this renders — falling back to
  // email — outside the app shell's SupportChatProvider.
  const supportChat = useContext(SupportChatContext);
  const [open, setOpen] = useState(false);
  const { release, items, reviewed, releaseAvailable, markReviewed, markDone } =
    whatsNew;

  if (!releaseAvailable) return null;

  const requiredItems = items.filter((view) => !view.item.optional);
  const doneCount = requiredItems.filter((view) => view.done).length;

  const handleCta = ({ item }: WhatsNewItemView) => {
    trackEvent("whats_new_cta_clicked", { itemId: item.id });
    markDone(item.id);
    setOpen(false);
    if (item.action.kind === "route") {
      navigate(item.action.route);
    } else if (supportChat) {
      supportChat.openSupportChat();
    } else {
      window.location.href = SUPPORT_MAILTO_URL;
    }
  };

  return (
    <>
      <ChecklistRow
        icon={Gift}
        title="Review the latest release"
        done={reviewed}
        muted={reviewed}
        description={
          !reviewed && doneCount > 0
            ? `See what's new in ${release.id} and how to try it. ${doneCount} of ${requiredItems.length} done.`
            : `See what's new in ${release.id} and how to try it.`
        }
        badges={
          reviewed ? (
            <Badge variant="secondary" className="text-[11px]">
              Done
            </Badge>
          ) : (
            <NewBadge />
          )
        }
        actions={
          <>
            <Button
              size="sm"
              variant={reviewed ? "outline" : "default"}
              onClick={() => setOpen(true)}
            >
              See what&apos;s new
            </Button>
            {!reviewed && (
              <Button size="sm" variant="ghost" onClick={markReviewed}>
                Mark as done
              </Button>
            )}
          </>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-5 py-4 pr-12 text-left sm:px-6">
            <DialogTitle>What&apos;s new in {release.id}</DialogTitle>
            <DialogDescription>
              Recently added to NFP Tracker — worth a look.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
            <ul className="space-y-3">
              {items.map((view) => {
                const Icon = view.item.icon;
                return (
                  <li
                    key={view.item.id}
                    aria-label={view.item.title}
                    className={`flex items-start gap-4 rounded-xl border p-4 sm:p-5 ${
                      view.done ? "bg-muted/30" : "bg-card"
                    }`}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      {view.done ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p
                          className={`text-base leading-tight ${
                            view.done
                              ? "font-medium text-foreground/80"
                              : "font-semibold"
                          }`}
                        >
                          {view.item.title}
                        </p>
                        {view.done ? (
                          <Badge variant="secondary" className="text-[11px]">
                            Done
                          </Badge>
                        ) : (
                          <NewBadge />
                        )}
                        {view.item.optional && !view.done ? (
                          <Badge variant="outline" className="text-[11px]">
                            Optional
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {view.item.description}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant={view.done ? "outline" : "default"}
                          onClick={() => handleCta(view)}
                        >
                          {view.item.ctaLabel}
                        </Button>
                        {!view.done && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markDone(view.item.id)}
                          >
                            Mark as done
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {release.notes.length > 0 && (
              <section
                aria-labelledby="release-notes-heading"
                className="mt-6 space-y-4 pb-2"
              >
                <h3
                  id="release-notes-heading"
                  className="text-base font-semibold"
                >
                  Release notes
                </h3>
                {release.notes.map((section) => (
                  <div key={section.heading}>
                    <h4 className="text-sm font-semibold text-foreground">
                      {section.heading}
                    </h4>
                    <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                      {section.items.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
